/* eslint-disable max-params */
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  addAdditionalService,
  addOrReplaceAdditionalServices,
  deleteAdditionalServices,
} from 'Store/booking/proposals/proposals.action';
import type { ServiceMutationType } from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import {
  excludePaidSlots,
  formatServiceToUpdateBookingCart,
} from 'Components/utils/booking/booking-additional-services/BookingAddtionalServices.utils';
import { useAppContext } from 'Context/ReactAppContext';
import {
  fetchBookingsCart,
  updateBookingCartSequencially,
} from 'Components/widgets/cart/cart.actions';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { CHILDCARE_TRACKING, TRACK_EVENT_NAMES } from 'Tracking/constants';

// COMMON TRACKING
// ON Error add Upsells
const trackErrorUnknownChildcare = (trackClickEvent) => {
  trackClickEvent(TRACK_EVENT_NAMES.CLICK_ERROR, {
    detail_click: CHILDCARE_TRACKING.ERROR_UNKNOWN_CHILDCARE,
  });
};

// advanced by day case
const getDaysOrFullStayLabelGA = (pricingInfo, attendees) => {
  const currentAttendee = attendees.find(
    ({ customerId = '', id = '' }) =>
      customerId === pricingInfo.childId || id === pricingInfo.childId,
  );
  const rentedSchedules = currentAttendee?.schedules;
  const rentedSchedulesNumber = rentedSchedules?.length || 0;

  if (
    (rentedSchedules && rentedSchedules?.length > 1) ||
    (rentedSchedulesNumber === 1 && rentedSchedules[0].startDate === rentedSchedules[0].endDate)
  ) {
    // here is the case of renting some n days but in the case of childcare by day
    return `dayslot_${rentedSchedulesNumber}`;
  }

  if (rentedSchedulesNumber === 1 && rentedSchedules[0].startDate !== rentedSchedules[0].endDate) {
    // here is the case of renting the full stay slot but in the case of childcare by day
    return 'all_journey';
  }
};

// morning afternoon or full day case for all stay case
const getMorningAfternoonOrFullDay = (pricingInfo, serviceId) => {
  const currentAttendee = pricingInfo.attendees.find(
    ({ id, customerId = '' }) => id === pricingInfo.childId || customerId === pricingInfo.childId,
  );
  const rentedSlotName = currentAttendee?.schedules[0].timeSlots?.find(
    ({ id }) => id === serviceId,
  )?.slotName;

  if (rentedSlotName === 'MORNING') {
    return 'morning';
  }

  if (rentedSlotName === 'AFTERNOON') {
    return 'afternoon';
  }

  if (rentedSlotName === 'DAY') {
    return 'all_day';
  }
};

// END COMMON TRACKING

/**
 * BE
 */
// get the childcare s context values for BE
export const useChidcareContext = ({
  proposals,
  appContext,
}: {
  proposals: ReadonlyArray<Proposal>;
  appContext: AppContext;
}) => {
  const dispatch = useDispatch();
  const { trackClickEvent } = useTrackEvent();
  const childcareContextServicesValuesBE = useMemo(
    () => ({
      currency: proposals?.[0]?.price?.currency,
      showRentAllSchedules: true,
      // FULL STAY SCHEDULE OR SINGLE DAY SCHEDULE
      // eslint-disable-next-line
      updateService: async (
        attendees: ServiceMutationType,
        pricingInfo: PricingInfoWithScheduleType,
        serviceIds: ReadonlyArray<string>,
        attendeeIds: ReadonlyArray<string>,
      ) => {
        if (pricingInfo.isPartiallyRented) {
          attendees.length === 0 &&
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
              detail_click: CHILDCARE_TRACKING.REMOVE_CHILDCARE,
            });
          dispatch(
            deleteAdditionalServices({
              appContext,
              attendeeIds,
              serviceIds,
              showNotification: true,
              proposals,
            }),
          );
        }

        if (attendees.length > 0) {
          const daysOrFullStayLabelGA = getDaysOrFullStayLabelGA(pricingInfo, attendees);
          return dispatch(
            addOrReplaceAdditionalServices({
              appContext,
              attendees,
              showNotification: true,
            }),
          )
            .then(() => {
              trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
                detail_click: CHILDCARE_TRACKING.ADD_CHILDCARE_POPIN,
                childcare_timeslot: daysOrFullStayLabelGA,
              });
            })
            .catch(() => trackErrorUnknownChildcare(trackClickEvent));
        }
      },
      deleteAttendeeChildcare: async ({
        attendeeId,
        pricingInfo,
      }: {
        attendeeId: string;
        pricingInfo: UpsellChildcarePricingInfo;
      }): Promise<any> => {
        const servicesIdsToRemove = pricingInfo.schedules[0].slots
          .map((slot) => slot.id)
          .filter(Boolean);

        if (servicesIdsToRemove.length > 0 && proposals && proposals?.length > 0) {
          return dispatch(
            deleteAdditionalServices({
              appContext,
              attendeeIds: [attendeeId],
              serviceIds: servicesIdsToRemove,
              showNotification: true,
              proposals,
            }),
          ).then(() => {
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
              detail_click: CHILDCARE_TRACKING.REMOVE_CHILDCARE,
            });
          });
        }
      },
      addAttendeeChildcare: ({
        attendeeId,
        pricingInfo,
        serviceId,
      }: {
        attendeeId: string;
        pricingInfo: UpsellChildcarePricingInfo;
        serviceId: string;
      }): Promise<any> => {
        const schedules = [
          {
            startDate: pricingInfo.schedules[0].startDate,
            endDate: pricingInfo.schedules[0].endDate,
          },
        ];
        return dispatch(
          addAdditionalService({
            proposalIds: proposals.map(({ id }) => id),
            serviceId,
            attendeeIds: [attendeeId],
            schedules,
            showNotification: true,
            appContext,
          }),
        )
          .then(() => {
            const morningAfternoonOrFullDayLabelGA = getMorningAfternoonOrFullDay(
              pricingInfo,
              serviceId,
            );
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
              detail_click: CHILDCARE_TRACKING.ADD_CHILDCARE_POPIN,
              childcare_timeslot: morningAfternoonOrFullDayLabelGA,
            });
          })
          .catch(() => trackErrorUnknownChildcare(trackClickEvent));
      },
    }),
    [],
  );
  return {
    childcareContextServicesValuesBE,
  };
};

/**
 * END BE
 */

/**
 * PLE
 */
export const useChildcareContextPLE = ({
  cartState,
  stay,
  addOrRemoveChildcareCC,
  cartChildcares,
}: {
  cartState: BookingsCart;
  stay: StayEntity;
  addOrRemoveChildcareCC: (arg0: {
    childcareId: string;
    attendeeId: string;
    startDate: string;
    endDate: string;
  }) => Promise<any>;
  cartChildcares: any;
}) => {
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const { trackClickEvent } = useTrackEvent();
  const childcareContextValuesPLE = useMemo(
    () => ({
      // Case update multi-schedule
      updateService: (
        updatedService: ServiceMutationType,
        pricingInfo: PricingInfoWithScheduleType,
        serviceIds: ReadonlyArray<string>,
      ) => {
        const alreadyPaidSchedulesNumber =
          pricingInfo.attendees
            .find(({ id }) => id === pricingInfo.childId)
            ?.schedules?.filter(({ isPartiallyPaid }) => isPartiallyPaid)?.length || 0;
        const isAdded = updatedService.some(
          (customerService) =>
            !!(customerService.schedules.length - alreadyPaidSchedulesNumber) &&
            customerService.customerId === pricingInfo.childId,
        );

        if (isAdded) {
          const daysOrFullStayLabelGA = getDaysOrFullStayLabelGA(pricingInfo, updatedService);
          trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
            detail_click: CHILDCARE_TRACKING.ADD_CHILDCARE_POPIN,
            childcare_timeslot: daysOrFullStayLabelGA,
          });
        } else {
          trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
            detail_click: CHILDCARE_TRACKING.REMOVE_CHILDCARE,
          });
        }

        // format the services to the adequat data structure
        const cart = formatServiceToUpdateBookingCart({
          appContext,
          bookingId: stay.booking.id,
          serviceIds,
          stay,
          type: pricingInfo.type,
          updatedService: excludePaidSlots(updatedService, stay.upsells.childcares),
        });
        return updateBookingCartSequencially(cart, cartState).finally(() => {
          return dispatch(
            fetchBookingsCart(
              (cartState?.cartBookings || []).map((b) => b.bookingId),
              appContext,
            ),
          );
        });
      },
      // Case delete and add ( different slots and one schedule )
      deleteAttendeeChildcare: async ({
        attendeeId,
        pricingInfo,
      }: {
        attendeeId: string;
        pricingInfo: UpsellChildcarePricingInfo;
      }) => {
        const currentAttendee = pricingInfo.attendees.find(({ id }) => id === attendeeId);
        const currentAttendeeRentedSlotsIds = currentAttendee.schedules[0].timeSlots
          ?.filter(({ isRented, isPaid }) => isRented && !isPaid)
          ?.map(({ id }) => id);

        if (currentAttendeeRentedSlotsIds?.length > 0) {
          currentAttendeeRentedSlotsIds.map((serviceId) => {
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
              detail_click: CHILDCARE_TRACKING.REMOVE_CHILDCARE,
            });
            // always deletes because we check if added to cart or not
            return addOrRemoveChildcareCC({
              childcareId: serviceId,
              attendeeId,
              startDate: pricingInfo.schedules[0].startDate,
              endDate: pricingInfo.schedules[0].endDate,
            });
          });
        }
      },
      addAttendeeChildcare: async ({
        attendeeId,
        pricingInfo,
        serviceId,
      }: {
        attendeeId: string;
        pricingInfo: UpsellChildcarePricingInfo;
        serviceId: string;
      }) => {
        const morningAfternoonOrFullDayLabelGA = getMorningAfternoonOrFullDay(
          pricingInfo,
          serviceId,
        );
        trackClickEvent(TRACK_EVENT_NAMES.CLICK_UPSELL, {
          detail_click: CHILDCARE_TRACKING.ADD_CHILDCARE_POPIN,
          childcare_timeslot: morningAfternoonOrFullDayLabelGA,
        });
        return addOrRemoveChildcareCC({
          childcareId: serviceId,
          attendeeId,
          startDate: pricingInfo.schedules[0].startDate,
          endDate: pricingInfo.schedules[0].endDate,
        });
      },
    }),
    [cartChildcares],
  );
  return {
    childcareContextValuesPLE,
  };
};
/**
 * END PLE
 */
